import React, { useEffect, useRef, useState } from 'react';
import { Wrapper, HeaderBar, Footer } from './Shared';
import { HOST_NAME, HOST_FIRST, TYP_VIDEO_EMBED_URL, TYP_VIDEO_POSTER_URL, TYP_YOUTUBE_URL, GHL_CALENDAR_EMBED_URL, WHATSAPP_URL } from './config';
import { track, firstName } from './pixel';
declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}
function youtubeId(url: string) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : /^[A-Za-z0-9_-]{11}$/.test(url) ? url : '';
}
function loadYouTubeApi(): Promise<any> {
  return new Promise(resolve => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev && prev();
      resolve(window.YT);
    };
    if (!document.getElementById('yt-iframe-api')) {
      const s = document.createElement('script');
      s.id = 'yt-iframe-api';
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  });
}
function SpeakerIcon() {
  return <svg viewBox="0 0 124 124" className="w-12 h-12 sm:w-[83px] sm:h-[83px] mx-auto mb-1 sm:mb-2.5" data-id="element-703" data-mp="cfv7guhkco23">
      <path d="M15.9429 51.4483V72.027C15.9429 74.8566 18.258 77.1717 21.0876 77.1717H36.5216L53.4477 94.0978C56.6889 97.3389 62.2451 95.0238 62.2451 90.445V32.9788C62.2451 28.4 56.6889 26.0849 53.4477 29.3261L36.5216 46.3036H21.0876C18.258 46.3036 15.9429 48.6187 15.9429 51.4483Z" fill="#fff" data-id="element-704" data-mp="r4j12v7umnpe" />
      <path className="wave1" d="M77.8535 44.6574C75.6575 42.6526 72.5352 44.5033 72.5352 47.4769V48.7404C72.5352 49.7317 72.9181 50.6765 73.5206 51.4637C75.7313 54.3518 77.0176 57.8017 77.0176 61.5208C77.0176 65.2383 75.7325 68.6867 73.5237 71.5684C72.9195 72.3566 72.5352 73.303 72.5352 74.2961V75.9864C72.5352 78.9505 75.641 80.8028 77.8369 78.812C82.4786 74.604 85.3969 68.5285 85.3969 61.7378C85.3969 54.9552 82.4856 48.8862 77.8535 44.6574Z" fill="#fff" data-id="element-705" data-mp="3pohlpd8y47lq" />
      <path className="wave2" d="M72.5352 22.8949V23.9239C72.5352 25.8789 73.8213 27.5766 75.622 28.2969C88.8953 33.5959 98.2586 46.612 98.2586 61.7374C98.2586 76.8628 88.8953 89.8789 75.622 95.1779C73.7699 95.8982 72.5352 97.5959 72.5352 99.5509V100.58C72.5352 103.821 75.7763 106.085 78.7602 104.953C96.2008 98.3162 108.548 81.493 108.548 61.7374C108.548 41.9818 96.2008 25.1586 78.7602 18.5219C75.7763 17.3387 72.5352 19.6538 72.5352 22.8949Z" fill="#fff" data-id="element-706" data-mp="1lyuyjwx4js70" />
    </svg>;
}
function UnmuteBox() {
  return <div className="absolute inset-0 flex items-center justify-center p-[6%] pointer-events-none" data-id="element-707" data-mp="187q5jdo9hy5n">
      <div className="unmute-box w-[62%] min-w-[200px] aspect-[16/9] flex flex-col items-center justify-center rounded-2xl border-2 border-white bg-[rgba(31,75,255,0.82)] px-3 text-center text-white" data-id="element-708" data-mp="1d4qcdqsxr4xt">
        <SpeakerIcon data-id="element-709" data-mp="kg15tc9427eo" />
        <h4 className="font-bold text-[13px] sm:text-[24px] md:text-[28px] leading-tight" data-id="element-710" data-mp="kgu7vzx4k8qh">Video Reproduciéndose<br data-id="element-711" data-mp="peckq1e9j83r" />Haz Clic Para Activar el Sonido</h4>
      </div>
    </div>;
}
function YouTubeSlot({
  id
}: {
  id: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [unmuted, setUnmuted] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then(YT => {
      if (cancelled || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId: id,
        width: '100%',
        height: '100%',
        host: 'https://www.youtube-nocookie.com',
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0
        },
        events: {
          onReady: (e: any) => {
            e.target.mute();
            e.target.playVideo();
          }
        }
      });
    });
    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy();
      } catch {}
    };
  }, [id]);
  const activate = () => {
    const p = playerRef.current;
    if (p && p.unMute) {
      p.unMute();
      p.setVolume(100);
      p.seekTo(0, true);
      p.playVideo();
    }
    setUnmuted(true);
  };
  return <div className="relative aspect-video rounded-2xl overflow-hidden bg-black" data-id="element-712" data-mp="6qhkmpdb5j">
      <div className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full" data-id="element-713" data-mp="4wx1vunkbikm"><div ref={hostRef} data-id="element-714" data-mp="3meq0z68k466q" /></div>
      {!unmuted && <div role="button" tabIndex={0} aria-label="Activar sonido" onClick={activate} onKeyDown={e => {
      if (e.key === 'Enter') activate();
    }} className="absolute inset-0 cursor-pointer" data-id="element-715" data-mp="3mdx02lwa4awi">
          <div className="absolute inset-0 bg-black/20" data-id="element-716" data-mp="3dc702roc03n4" />
          <UnmuteBox data-id="element-717" data-mp="3icl7c7i3z9k3" />
        </div>}
    </div>;
}
function GenericSlot() {
  const [playing, setPlaying] = useState(false);
  const src = TYP_VIDEO_EMBED_URL ? TYP_VIDEO_EMBED_URL + (TYP_VIDEO_EMBED_URL.includes('?') ? '&' : '?') + 'autoplay=true&muted=false' : '';
  if (playing && src) {
    return <div className="relative aspect-video rounded-2xl overflow-hidden bg-black" data-id="element-718" data-mp="11nzd0uaee7yy">
        <iframe src={src} className="absolute inset-0 w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="Video" data-id="element-719" data-mp="16nu2quifsrif" />
      </div>;
  }
  return <div role="button" tabIndex={0} onClick={() => setPlaying(true)} onKeyDown={e => {
    if (e.key === 'Enter') setPlaying(true);
  }} className="relative aspect-video rounded-2xl overflow-hidden bg-gray-700 cursor-pointer" data-id="element-720" data-mp="2m69v0jk5prz2">
      {TYP_VIDEO_POSTER_URL ? <img src={TYP_VIDEO_POSTER_URL} alt="" width={1280} height={720} decoding="async" className="absolute inset-0 w-full h-full object-cover" data-id="element-721" data-mp="2w3nxtgd5qi8w" /> : <div className="absolute inset-0 bg-[#0B0D12] flex items-end justify-end p-3" data-id="element-722" data-mp="35u94lr2zit2w">
          <span className="text-[11px] font-semibold text-white/50" data-id="element-723" data-mp="35wlk7xl9srcs">[VIDEO EXPLICATIVO — pega el link en funnel/config.ts]</span>
        </div>}
      <div className="absolute inset-0 bg-black/25" data-id="element-724" data-mp="3evywey0s3u17" />
      <UnmuteBox data-id="element-725" data-mp="3et498yqdrv43" />
    </div>;
}
function VideoSlot() {
  const yt = youtubeId(TYP_YOUTUBE_URL);
  return yt ? <YouTubeSlot id={yt} data-id="element-726" data-mp="1kgweble27e8b" /> : <GenericSlot data-id="element-727" data-mp="1khpmmqso2bsk" />;
}

/* PÁGINA DE AGENDA: video explicativo + calendario (sin bloquear) */
export function ThankYou() {
  const name = firstName();
  useEffect(() => {
    track('CompleteRegistration');
    window.scrollTo(0, 0);
  }, []);
  return <div className="typ min-h-screen bg-white text-[#1a1a1a]" data-id="element-728" data-mp="3pkctr1fo03ur">
      <HeaderBar data-id="element-729" data-mp="3tisndo694ah5" />
      <div className="px-3 sm:px-6 pt-6 sm:pt-10 pb-12" data-id="element-730" data-mp="3tialaawwr21b">
        <div className="mx-auto max-w-[1000px] rounded-[28px] bg-white shadow-[0_0_30px_rgba(0,0,0,0.08)] px-4 py-6 sm:px-[7%] sm:py-12 text-center" data-id="element-731" data-mp="2e2e59w68ejz">
          <h2 className="text-[22px] sm:text-[38px] font-bold uppercase leading-tight tracking-tight" data-id="element-732" data-mp="7cdormuait39">
            {name && <>{name}, </>}<span className="inline-block" data-id="element-733" data-mp="h2f9q5rcjvdy">¡Ya casi! Mira este video</span>
          </h2>
          <p className="mt-2 mb-6 text-[15px] sm:text-[22px] text-gray-700" data-id="element-734" data-mp="h4t5yhxrjdn3">y después elige tu horario aquí abajo</p>

          <VideoSlot data-id="element-735" data-mp="q3dggvt66h08" />

          <div className="mt-6 sm:mt-8 mx-auto inline-block max-w-full bg-brand-light px-4 sm:px-14 py-3 sm:py-4 font-bold text-[15px] sm:text-[22px] leading-snug text-[#333]" data-id="element-736" data-mp="q3wrdskek54r">
            Llamada 1:1 con {HOST_FIRST} <span className="block font-medium" data-id="element-737" data-mp="1576fnzyban1v">20–30 minutos · desde tu computadora</span>
          </div>

          <p className="mt-5 sm:mt-7 text-[14px] sm:text-[22px] leading-[1.45]" data-id="element-738" data-mp="106agu0sthr3b">
            En el video te explico <strong data-id="element-739" data-mp="v8han7rikx31">cómo funciona todo, los costos y qué esperar</strong> de nuestra llamada. <strong data-id="element-740" data-mp="r7f9umhy8cyy">Después elige tu horario</strong> y te escribo por <strong data-id="element-741" data-mp="1nwmvrvmhco3e">WhatsApp para confirmar.</strong>
          </p>

          <div id="agenda" className="mt-8 text-left" data-id="element-742" data-mp="1nw4ng0mjmjf4">
            {GHL_CALENDAR_EMBED_URL ? <iframe src={GHL_CALENDAR_EMBED_URL} title="Calendario" className="w-full min-h-[760px] rounded-xl border-0" scrolling="no" data-id="element-743" data-mp="1iy35jj0usq58" /> : <div className="min-h-[420px] rounded-xl border-2 border-dashed border-brand/40 bg-brand-light flex items-center justify-center text-center px-6 text-sm font-semibold text-brand/70" data-id="element-744" data-mp="1a5rkkcoiduwn">[CALENDARIO GHL — pega la URL en funnel/config.ts]</div>}
          </div>

          <p className="mt-6 sm:mt-8 text-[13px] sm:text-[20px]" data-id="element-745" data-mp="2lkg167xhoz7d">
            <em className="bg-brand-light px-1 not-italic" data-id="element-746" data-mp="2lj50wi3969l0">(<strong data-id="element-747" data-mp="2qboevg5ur6qc">Importante</strong> — las personas que ven el video antes de la llamada son <span className="inline-block" data-id="element-748" data-mp="2bma0ftnf5z43">las que más la aprovechan.)</span></em>
          </p>
          <p className="mt-4 text-[13px] text-gray-500" data-id="element-749" data-mp="2glcygu4zf52k">Agendar una llamada no garantiza aceptación, licencia ni ingresos.</p>
          <p className="mt-2 text-[14px]" data-id="element-750" data-mp="34ao1ki9t1bjv"><a href={WHATSAPP_URL} className="text-brand font-bold underline underline-offset-4" data-id="element-751" data-mp="34bgyysmviopc">¿Prefieres escribirme por WhatsApp?</a></p>

          <div className="mt-5 sm:mt-7" data-id="element-752" data-mp="2ucrrltslsmnc">
            <p className="typ-signature text-[40px] sm:text-[64px] leading-none text-brand" data-id="element-753" data-mp="2ubq7kztm0pth">{HOST_FIRST}</p>
          </div>
        </div>
      </div>
      <Footer data-id="element-754" data-mp="2zdnxue0fgkvx" />
    </div>;
}

/* CONFIRMADA: redirección post-reserva desde GHL a #/confirmada */
export function Confirmed() {
  const name = firstName();
  useEffect(() => {
    track('Schedule');
    window.scrollTo(0, 0);
  }, []);
  return <div className="typ min-h-screen bg-white text-[#1a1a1a]" data-id="element-755" data-mp="5y9nilogsilq">
      <HeaderBar data-id="element-756" data-mp="zyeue9woe53" />
      <div className="px-3 sm:px-6 pt-6 sm:pt-10 pb-12" data-id="element-757" data-mp="10hlgyu3vg3t">
        <div className="mx-auto max-w-[1000px] rounded-[28px] bg-white shadow-[0_0_30px_rgba(0,0,0,0.08)] px-4 py-8 sm:px-[7%] sm:py-12 text-center" data-id="element-758" data-mp="122g0ogf6z1o">
          <h2 className="text-[24px] sm:text-[40px] font-bold uppercase leading-tight tracking-tight" data-id="element-759" data-mp="axw0qv4alyuj">
            {name && <>{name} </>}<span className="inline-block" data-id="element-760" data-mp="3jeqrjc7tfss9">¡Tu llamada está confirmada!</span>
          </h2>
          <div className="mt-5 sm:mt-7 mx-auto inline-block max-w-full bg-brand-light px-4 sm:px-14 py-3 sm:py-4 font-bold text-[15px] sm:text-[22px] leading-snug text-[#333]" data-id="element-761" data-mp="3eg5s3f0e1d4v">
            Llamada 1:1 con {HOST_NAME}
          </div>
          <p className="mt-5 sm:mt-7 text-[14px] sm:text-[22px] leading-[1.45]" data-id="element-762" data-mp="3ndg4x4sutabw">
            <strong data-id="element-763" data-mp="2zoezlqfbifau">Revisa tu WhatsApp:</strong> te escribo para confirmar. <strong data-id="element-764" data-mp="2vp6aiyv835lm">Si no respondes, liberamos tu espacio.</strong>
            <br data-id="element-765" data-mp="7e059ditderm" />Conéctate <strong data-id="element-766" data-mp="7dqdpli4ezjt">desde computadora</strong>, en un lugar tranquilo, y trae tus preguntas sobre <strong data-id="element-767" data-mp="2evx8q7mln2v">costos, licencia y horarios.</strong>
          </p>
          <p className="mt-6 sm:mt-8 text-[13px] sm:text-[20px]" data-id="element-768" data-mp="2fou8zgs8y7t">
            <em className="bg-brand-light px-1 not-italic" data-id="element-769" data-mp="3uhn2xhhvq0td">(<strong data-id="element-770" data-mp="3ukj0h4ptenha">Importante</strong> — si aún no viste el video, míralo antes de la llamada.)</em>
          </p>
          <div className="mt-5 sm:mt-7" data-id="element-771" data-mp="3kkiefb0k9t9e"><p className="typ-signature text-[40px] sm:text-[64px] leading-none text-brand" data-id="element-772" data-mp="3kks3mwds7we1">{HOST_FIRST}</p></div>
        </div>
      </div>
      <Footer data-id="element-773" data-mp="3fsh4hvqv8emj" />
    </div>;
}